import React from 'react';
import {Swiper, SwiperClass} from 'swiper/react';
import {
  Text,
  TextAlignment,
  TokenTextAppearance,
  CTA,
} from '@volkswagen-onehub/components-core';
import SpaceLike from '../../components/Space';
import styled from 'styled-components';
import StepOneDiagram from './svg-diagrams/step-1-diagram';
import StepTwoDiagram from './svg-diagrams/step-2-diagram';
import StepThreeDiagram from './svg-diagrams/step-3-diagram';
import Step from './step';
import {Steps} from '../app-screen';
import 'swiper/css/effect-fade';
import StepFourDiagram from './svg-diagrams/step-4-diagram';
import {useSendAmpChargeAnalytic} from '../../hooks/useSendAmpChargeAnalytic';

const StyledText = styled(Text)`
  color: white;
`;

const StyledCta = styled(CTA)`
  color: white;
`;

type Props = {
  setStep: (step: Steps) => void;
  isCustomers?: boolean;
};

const WalkthroughSwiper: React.FC<Props> = ({setStep, isCustomers}) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const {handleSkipWalkThroughClick} = useSendAmpChargeAnalytic();

  const onSkipClick = () => {
    setStep(Steps.CHOOSE);
    handleSkipWalkThroughClick();
  };

  return (
    <SpaceLike
      direction="vertical"
      customStyles={{
        textAlign: 'center',
        justifyContent: 'center',
        paddingBottom: 'calc(50px + env(safe-area-inset-bottom))',
      }}
    >
      <StyledText
        appearance={TokenTextAppearance.label200}
        textAlign={TextAlignment.center}
        bold
      >
        How to use
      </StyledText>
      <Swiper
        spaceBetween={100}
        slidesPerView={1}
        pagination={{clickable: true, type: 'bullets'}}
        onSlideChange={(swiper: SwiperClass) =>
          setActiveIndex(swiper.activeIndex)
        }
        direction="horizontal"
        style={
          {
            'width': '100%',
            'height': '100%',
            '--swiper-pagination-bottom': '24px',
            '--swiper-pagination-color': '#001E50',
            '--swiper-pagination-bullet-inactive-color': '#001E50',
            '--swiper-pagination-bullet-inactive-opacity': '0.5',
            '--swiper-pagination-bullet-horizontal-gap': '8px',
          } as React.CSSProperties
        }
      >
        <Step
          title="Find your perfect placement"
          description="Using the guides, set the lower edge of the charger between 90–150cm from the ground."
          index={0}
          isActive={activeIndex === 0}
        >
          <StepOneDiagram />
        </Step>
        <Step
          title="Check your surroundings"
          description="Use the guides to ensure there is sufficient clearance space around your charger."
          index={1}
          isActive={activeIndex === 1}
        >
          <StepTwoDiagram isActive={activeIndex === 1} />
        </Step>
        <Step
          title={'Safe and suitable'}
          description={
            isCustomers
              ? 'Make sure you’ve picked a solid and suitable wall to mount your charger onto. This will also need to be inspected by a licensed electrician on the day of installation.'
              : 'Make sure you picked a solid and suitable wall to mount your charger onto.'
          }
          index={2}
          isActive={activeIndex === 2}
        >
          <StepThreeDiagram isActive={activeIndex === 2} />
        </Step>
        {isCustomers && (
          <Step
            title="When you're ready, snap a picture"
            description="Once you've found your most suitable spot, snap some photos so we know where you want your charger to go."
            index={3}
            isActive={activeIndex === 3}
          >
            <StepFourDiagram isActive={activeIndex === 3} />
          </Step>
        )}
      </Swiper>
      <StyledCta onClick={onSkipClick} tag="button" emphasis="tertiary">
        Skip
      </StyledCta>
    </SpaceLike>
  );
};

export default WalkthroughSwiper;
